import {
	useState,
	useRef,
	useLayoutEffect,
	useCallback,
	type CSSProperties,
	type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { HelpCircle } from "lucide-react";

interface TooltipProps {
	content: string | ReactNode;
	children: ReactNode;
	position?: "top" | "bottom" | "left" | "right";
	maxWidth?: string;
	className?: string;
	showIcon?: boolean;
}

/** Above admin sidebar (z-40), mobile menu (z-50), and most modals */
const TOOLTIP_PORTAL_Z = 10050;

function getTooltipStyle(
	position: TooltipProps["position"],
	rect: DOMRect,
): CSSProperties {
	const cx = rect.left + rect.width / 2;
	const cy = rect.top + rect.height / 2;
	switch (position) {
		case "bottom":
			return {
				position: "fixed",
				left: cx,
				top: rect.bottom + 8,
				transform: "translate(-50%, 0)",
				zIndex: TOOLTIP_PORTAL_Z,
			};
		case "left":
			return {
				position: "fixed",
				left: rect.left - 8,
				top: cy,
				transform: "translate(-100%, -50%)",
				zIndex: TOOLTIP_PORTAL_Z,
			};
		case "right":
			return {
				position: "fixed",
				left: rect.right + 8,
				top: cy,
				transform: "translate(0, -50%)",
				zIndex: TOOLTIP_PORTAL_Z,
			};
		case "top":
		default:
			return {
				position: "fixed",
				left: cx,
				top: rect.top - 8,
				transform: "translate(-50%, -100%)",
				zIndex: TOOLTIP_PORTAL_Z,
			};
	}
}

function Arrow({
	position,
}: Readonly<{ position: TooltipProps["position"] }>): JSX.Element {
	const arrowClasses = {
		top: "top-full left-1/2 -translate-x-1/2 border-t-gray-800 border-l-transparent border-r-transparent border-b-transparent",
		bottom:
			"bottom-full left-1/2 -translate-x-1/2 border-b-gray-800 border-l-transparent border-r-transparent border-t-transparent",
		left: "left-full top-1/2 -translate-y-1/2 border-l-gray-800 border-t-transparent border-b-transparent border-r-transparent",
		right:
			"right-full top-1/2 -translate-y-1/2 border-r-gray-800 border-t-transparent border-b-transparent border-l-transparent",
	};
	const pos = position ?? "top";
	return (
		<div
			className={`absolute w-0 h-0 border-4 ${arrowClasses[pos]}`}
			aria-hidden
		/>
	);
}

export const Tooltip = ({
	content,
	children,
	position = "top",
	maxWidth = "280px",
	className = "",
	showIcon = false,
}: TooltipProps) => {
	const [isVisible, setIsVisible] = useState(false);
	const triggerRef = useRef<HTMLDivElement>(null);
	const [bubbleStyle, setBubbleStyle] = useState<CSSProperties | null>(null);

	const updatePosition = useCallback(() => {
		const el = triggerRef.current;
		if (!el) return;
		const rect = el.getBoundingClientRect();
		setBubbleStyle(getTooltipStyle(position, rect));
	}, [position]);

	useLayoutEffect(() => {
		if (!isVisible) {
			setBubbleStyle(null);
			return;
		}
		updatePosition();
		const opts = { capture: true };
		const scheduleUpdate = () => {
			requestAnimationFrame(updatePosition);
		};
		window.addEventListener("scroll", scheduleUpdate, opts);
		window.addEventListener("resize", scheduleUpdate);
		return () => {
			window.removeEventListener("scroll", scheduleUpdate, opts);
			window.removeEventListener("resize", scheduleUpdate);
		};
	}, [isVisible, updatePosition]);

	const bubble =
		isVisible &&
		bubbleStyle &&
		typeof document !== "undefined" &&
		createPortal(
			<div
				className="pointer-events-none"
				style={bubbleStyle}
				role="tooltip"
			>
				<div
					className="relative bg-gray-800 text-white text-xs rounded-lg px-3 py-2 shadow-lg whitespace-normal break-words"
					style={{
						maxWidth,
						minWidth: "200px",
						width: "max-content",
					}}
				>
					{typeof content === "string" ? (
						<p className="leading-relaxed">{content}</p>
					) : (
						<div className="leading-relaxed">{content}</div>
					)}
					<Arrow position={position} />
				</div>
			</div>,
			document.body,
		);

	return (
		<>
			<div
				ref={triggerRef}
				className={`relative inline-block ${className}`}
				onMouseEnter={() => setIsVisible(true)}
				onMouseLeave={() => setIsVisible(false)}
				onFocus={() => setIsVisible(true)}
				onBlur={() => setIsVisible(false)}
			>
				{showIcon ? (
					<div className="flex items-center gap-1">
						{children}
						<HelpCircle
							size={14}
							className="text-gray-400 hover:text-gray-600 cursor-help"
							aria-label="Help"
						/>
					</div>
				) : (
					children
				)}
			</div>
			{bubble}
		</>
	);
};
